import React from 'react';
import { ArrowLeftRight, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { EmptyState, StatusPill, type StatusTone } from '../../../components/app';
import { FounderTransactionKind } from '../../../api/treasury';
import { TransactionsTableProps } from '../types';

export const KIND_LABELS: Record<FounderTransactionKind, string> = {
  contribution: 'Взнос на счёт',
  reimbursement: 'Возврат вклада',
  profit: 'Доля прибыли',
};

const KIND_TONES: Record<FounderTransactionKind, StatusTone> = {
  contribution: 'info',
  reimbursement: 'warn',
  profit: 'pos',
};

const day = (d: string) => d.slice(0, 10).split('-').reverse().join('.');

export function TransactionsTable({ transactions, isLoading, onEdit, onDelete, formatMoney }: TransactionsTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Дата</TableHead>
          <TableHead>Основатель</TableHead>
          <TableHead>Вид</TableHead>
          <TableHead className="text-right">Сумма</TableHead>
          <TableHead>Комментарий</TableHead>
          <TableHead className="w-[1%]">
            <span className="sr-only">Действия</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array.from({ length: 4 }, (_, i) => (
            <TableRow key={i}>
              {Array.from({ length: 6 }, (__, j) => (
                <TableCell key={j}>
                  <Skeleton className="h-4 w-full max-w-28" />
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : transactions.length === 0 ? (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={6}>
              <EmptyState
                icon={<ArrowLeftRight />}
                title="Движений пока нет"
                description="Деньги со Счёта компании никто не брал и не вносил."
              />
            </TableCell>
          </TableRow>
        ) : (
          transactions.map((transaction) => (
            <TableRow key={transaction.id}>
              <TableCell className="whitespace-nowrap tabular-nums">{day(transaction.date)}</TableCell>
              <TableCell className="font-semibold">{transaction.payer?.name || '—'}</TableCell>
              <TableCell>
                <StatusPill size="sm" tone={KIND_TONES[transaction.kind]}>
                  {KIND_LABELS[transaction.kind]}
                </StatusPill>
              </TableCell>
              <TableCell className="whitespace-nowrap text-right font-semibold tabular-nums">
                {formatMoney(transaction.amount)}
              </TableCell>
              <TableCell className="max-w-xs truncate text-text-2">{transaction.comment || '—'}</TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon-sm" onClick={() => onEdit(transaction)} aria-label="Изменить движение">
                    <Pencil className="text-text-2" />
                  </Button>
                  <Button variant="ghost" size="icon-sm" onClick={() => onDelete(transaction.id)} aria-label="Удалить движение">
                    <Trash2 className="text-neg" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
